import moment from "moment";

/**
 * Helpers compartidos por los parsers de PDF de proveedor.
 *
 * Nacieron al agregar PISA, Salubox y Sabela: los tres traen la caducidad en un formato
 * distinto (ISO, mes en inglés, dd-mm-aa) y los tres necesitan la misma aritmética de
 * validación que ya usaba Cotizacion.js. En vez de una cuarta copia de cada cosa, vive aquí.
 *
 * Los parsers viejos (Nadro, Rosario, Levic, Fanasa) siguen con sus helpers propios: no se
 * tocaron para no arriesgar formatos que ya funcionan en producción.
 */

const TOLERANCIA = 0.01; // 1% de holgura por redondeos del proveedor

// Ventana de plausibilidad de una caducidad, en años respecto a hoy. Es lo que evita
// confundirla con la fecha de emisión de la factura, un código postal o un folio.
const ANIOS_ATRAS = 5;
const ANIOS_ADELANTE = 25;

// Meses en español e inglés. Explícitos a propósito: la app hace moment.locale("es")
// globalmente (RecibirInsumos.js:71 y ~200 archivos más), así que "30-Apr-2028" de Salubox
// NO parsea con el token MMM de moment. Nunca depender del locale aquí.
const MESES = {
  ene: 0, enero: 0, jan: 0, january: 0,
  feb: 1, febrero: 1, february: 1,
  mar: 2, marzo: 2, march: 2,
  abr: 3, abril: 3, apr: 3, april: 3,
  may: 4, mayo: 4,
  jun: 5, junio: 5, june: 5,
  jul: 6, julio: 6, july: 6,
  ago: 7, agosto: 7, aug: 7, august: 7,
  sep: 8, sept: 8, septiembre: 8, september: 8,
  oct: 9, octubre: 9, october: 9,
  nov: 10, noviembre: 10, november: 10,
  dic: 11, diciembre: 11, dec: 11, december: 11,
};

// Palabras de unidad que se pegan a la cantidad y no son parte de la descripción.
// Misma lista que Cotizacion.js:25.
export const UNIDADES =
  /^(PZ|PZA|PZAS|PIEZA|PIEZAS|KG|KGS|GR|GRS|LT|LTS|MT|MTS|M|M2|M3|CM|ML|CJA|CAJA|CAJAS|PAQ|PQT|BOL|BOLSA|SERV|SERVICIO|JGO|JUEGO|ROLLO|TRAMO|UND|UNID|UNIDAD|TON)$/i;

// Líneas que traen números pero nunca son partidas. Base tomada de Cotizacion.js:20, más
// lo que aparece en una factura timbrada y no en una cotización (sellos, cadena original).
const LINEAS_IGNORADAS =
  /^(SUB\s*-?\s*TOTAL|TOTAL|GRAN\s+TOTAL|I\.?\s*V\.?\s*A\b|IMPUEST|DESCUENT|SUMA|RETENC|ANTICIPO|SALDO|TEL[EÉ]?F?|FOLIO|R\.?F\.?C|C\.?P\.?\s|P[AÁ]GINA|CONTACTO|DIRECCI[OÓ]N|VIGENC|CONDICION|TIEMPO\s+DE\s+ENTREGA|SELLO|CADENA\s+ORIGINAL|CERTIFICADO|N[ÚU]MERO\s+SERIE|UUID|R[ÉE]GIMEN|USO\s+DE\s+CFDI|M[ÉE]TODO\s+DE\s+PAGO|FORMA\s+DE\s+PAGO|LUGAR\s+DE\s+EXPEDICI|MONEDA|OBSERVACIONES|IMPORTE\s+CON\s+LETRA|BANCO|CUENTA|CLABE)/i;

// Marca de caducidad: una línea que la menciona nunca es ruido, aunque empiece con "Fecha"
// (PISA imprime "Fecha Caducidad: 2028-03-01" en un renglón aparte).
const MARCA_CADUCIDAD = /CADUCIDAD|CADUC\b|CAD\s*\.|FECHA\s+CAD/i;

const REGEX_ACENTOS_NFD = /[̀-ͯ]/g;

/**
 * Formatos de fecha que aparecen en estas facturas, para BUSCAR dentro de una línea.
 * A propósito NO incluye "mes/año" suelto (junio/2027, formato de Rosario): en un buscador
 * genérico cualquier palabra seguida de un año daría un falso positivo. parsearFechaCaducidad
 * sí lo acepta cuando se le pasa el token completo.
 */
const REGEX_FECHA_EN_LINEA = new RegExp(
  [
    "(?<!\\d)\\d{4}-\\d{2}-\\d{2}(?!\\d)", // 2028-03-01  (PISA)
    "(?<!\\d)\\d{1,2}[-/][A-Za-zÁÉÍÓÚÑáéíóúñ]{3,10}[-/]\\d{2,4}(?!\\d)", // 30-Apr-2028 (Salubox), 31-JUL-27 (Fanasa)
    "(?<!\\d)\\d{1,2}[-/]\\d{1,2}[-/]\\d{2,4}(?!\\d)", // 31-07-27 (Sabela), 31/07/2027
  ].join("|"),
  "g"
);

const aCuatroDigitos = (anio) => (anio < 100 ? 2000 + anio : anio);

const esAnioPlausible = (anio) => {
  const actual = moment().year();
  return anio >= actual - ANIOS_ATRAS && anio <= actual + ANIOS_ADELANTE;
};

/**
 * Convierte a moment cualquiera de los formatos de caducidad que imprimen los proveedores.
 * Devuelve null si no parsea o si el año cae fuera de la ventana plausible.
 *
 * Soporta: 2028-03-01 | 30-Apr-2028 | 31-JUL-27 | 31-07-27 | 31/07/2027 | 31/ene/2028 | junio/2027
 *
 * @param {string} texto
 * @returns {import('moment').Moment | null}
 */
export function parsearFechaCaducidad(texto) {
  const str = String(texto ?? "").trim();
  if (!str) return null;

  let anio = null;
  let mes = null;
  let dia = 1;

  const iso = str.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const alfabetico = str.match(/^(\d{1,2})[-/]([A-Za-zÁÉÍÓÚÑáéíóúñ]{3,10})[-/](\d{2,4})$/);
  const numerico = str.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{2,4})$/);
  const mesAnio = str.match(/^(?:(\d{1,2})\/)?([A-Za-zÁÉÍÓÚÑáéíóúñ]{3,10})\/(\d{4})$/);

  if (iso) {
    anio = parseInt(iso[1], 10);
    mes = parseInt(iso[2], 10) - 1;
    dia = parseInt(iso[3], 10);
  } else if (alfabetico) {
    dia = parseInt(alfabetico[1], 10);
    mes = MESES[normalizarClave(alfabetico[2])];
    anio = aCuatroDigitos(parseInt(alfabetico[3], 10));
  } else if (numerico) {
    const primero = parseInt(numerico[1], 10);
    const segundo = parseInt(numerico[2], 10);
    // dd-mm vs mm-dd: manda el que no puede ser mes. Si ambos caben, se asume dd-mm,
    // que es lo que imprime cualquier factura mexicana.
    if (segundo > 12 && primero <= 12) {
      dia = segundo;
      mes = primero - 1;
    } else {
      dia = primero;
      mes = segundo - 1;
    }
    anio = aCuatroDigitos(parseInt(numerico[3], 10));
  } else if (mesAnio) {
    dia = mesAnio[1] ? parseInt(mesAnio[1], 10) : 1;
    mes = MESES[normalizarClave(mesAnio[2])];
    anio = parseInt(mesAnio[3], 10);
  } else {
    return null;
  }

  if (mes === undefined || mes === null || mes < 0 || mes > 11) return null;
  if (!(dia >= 1 && dia <= 31)) return null;
  if (!esAnioPlausible(anio)) return null;

  const fecha = moment({ year: anio, month: mes, day: dia });
  return fecha.isValid() ? fecha : null;
}

/**
 * Busca la primera caducidad plausible dentro de una fila y dice dónde está, para poder
 * quitarla del texto antes de leer la descripción ("extraer y quitar").
 *
 * @param {string} linea
 * @returns {{ fecha: import('moment').Moment, texto: string, indice: number } | null}
 */
export function buscarFechaCaducidad(linea) {
  const str = String(linea ?? "");
  REGEX_FECHA_EN_LINEA.lastIndex = 0;
  let match;
  while ((match = REGEX_FECHA_EN_LINEA.exec(str)) !== null) {
    const fecha = parsearFechaCaducidad(match[0]);
    if (fecha) return { fecha, texto: match[0], indice: match.index };
  }
  return null;
}

/**
 * Clave de comparación entre el PDF y el catálogo: minúsculas, sin acentos y con los
 * espacios colapsados, para que "Tornillo  Galvanizado" empate con "TORNILLO GALVANIZADO".
 * Mismo criterio que normalizarDescripcionInsumo del backend (Utils/insumos.utils.js:60).
 */
export function normalizarClave(texto) {
  return String(texto ?? "")
    .normalize("NFD")
    .replace(REGEX_ACENTOS_NFD, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

/**
 * ¿La fila habla de una caducidad? Sirve para distinguir el renglón de continuación de una
 * partida ("Fecha Caducidad: 2028-03-01") de la fecha de vencimiento de la propia factura,
 * que no tiene nada que ver con el lote.
 */
export function mencionaCaducidad(linea) {
  return MARCA_CADUCIDAD.test(String(linea ?? ""));
}

/** Filas con números que nunca son partidas (totales, sellos, datos fiscales). */
export function esRuido(linea) {
  const str = String(linea ?? "").trim();
  if (!str) return true;
  if (MARCA_CADUCIDAD.test(str)) return false;
  return LINEAS_IGNORADAS.test(str);
}

export const limpiarNumero = (token) =>
  String(token ?? "")
    .replace(/\$/g, "")
    .replace(/,/g, "")
    .trim();

export const esTokenNumerico = (token) => {
  const limpio = limpiarNumero(token);
  if (!limpio) return false;
  // Un número con ceros a la izquierda ("06600", "0012") es un código postal o un folio,
  // nunca una cantidad ni un precio
  if (/^0\d/.test(limpio)) return false;
  return /^-?\d+(\.\d+)?$/.test(limpio);
};

export const aNumero = (token) => parseFloat(limpiarNumero(token));

/** cantidad x precio ~= importe, con holgura relativa. */
export const cuadraImporte = (cantidad, precio, importe) => {
  if (!(cantidad > 0) || !(precio > 0)) return false;
  const esperado = cantidad * precio;
  return Math.abs(esperado - importe) <= esperado * TOLERANCIA;
};

export const contarLetras = (texto) =>
  (String(texto).match(/[A-Za-zÁÉÍÓÚÜÑáéíóúüñ]/g) || []).length;

/**
 * Acumula un lote en una entrada del Map de resultados, sumando si el lote se repite.
 * Es el mismo criterio de Rosario.js:81-86 y Fanasa.js:42-47.
 */
export function agregarLote(entrada, numero, caducidad, cantidad) {
  if (!numero) return;
  const existente = entrada.lotes.find((l) => l.numero === numero);
  if (existente) {
    if (cantidad != null) {
      existente.cantidad = (Number(existente.cantidad) || 0) + cantidad;
    }
    if (!existente.caducidad && caducidad) existente.caducidad = caducidad;
    return;
  }
  entrada.lotes.push({ numero, caducidad: caducidad || null, cantidad: cantidad ?? null });
}

/** Registra una entrada bajo una clave, sin pisar la que ya estuviera (gana la primera). */
export function registrarClave(mapa, clave, entrada) {
  const key = normalizarClave(clave);
  if (!key) return;
  if (!mapa.has(key)) mapa.set(key, entrada);
}

/**
 * Acumula un producto en el Map de resultados: agrupa por descripción normalizada, registra
 * el código del proveedor como alias hacia la MISMA entrada (para que el emparejamiento
 * pueda usar cualquiera de los dos) y suma el lote.
 *
 * Un producto con lote siempre emite lote, aunque falte la caducidad o la cantidad: medio
 * dato prellenado le ahorra tecleo al usuario, y la pantalla ya marca el descuadre.
 */
export function acumularProducto(mapa, { descripcion, codigo, lote, caducidad, cantidad }) {
  const clave = normalizarClave(descripcion);
  if (!clave) return null;

  if (!mapa.has(clave)) mapa.set(clave, { lotes: [], cantidadLinea: null });
  const entrada = mapa.get(clave);
  if (codigo) registrarClave(mapa, codigo, entrada);

  if (lote) {
    agregarLote(entrada, lote, caducidad, cantidad);
  } else if (cantidad > 0) {
    entrada.cantidadLinea = (entrada.cantidadLinea || 0) + cantidad;
  }

  return entrada;
}
